// helpers/buildImpacts.ts
import { buildOption } from "@/components/FormEngine/helpers/buildOptions";
import {
  FieldImpact,
  FormOption,
  RawField,
} from "@/components/FormEngine/types";
import { getString, isObject } from "@/components/FormEngine/utils";

// ========================================
// TYPES ET INTERFACES
// ========================================

/**
 * Structure d'un impact brut provenant d'une source externe
 * Contient le nom du champ cible et les propriétés à modifier
 */
interface RawImpactData {
  /** Nom du champ impacté */
  name?: unknown;
  /** Options possibles pour le champ (si c'est un select/radio) */
  options?: unknown;
  /** Autres propriétés à appliquer au champ */
  [propertyKey: string]: unknown;
}

/**
 * Impact par défaut retourné en cas d'erreur
 * Représente un impact vide sans effet
 */
const DEFAULT_EMPTY_IMPACT: FieldImpact = {
  id: "",
  path: [],
  name: "",
  properties: {} as Partial<RawField>,
};

// ========================================
// FONCTION PRINCIPALE
// ========================================

/**
 * Construit un impact de champ normalisé à partir de données brutes
 *
 * Un impact décrit comment un champ du formulaire doit être modifié
 * lorsqu'une certaine condition est remplie (ex: sélection d'une option).
 *
 * Cette fonction :
 * 1. Valide les données d'entrée
 * 2. Extrait le nom du champ cible
 * 3. Extrait et normalise les propriétés à appliquer
 * 4. Traite les options si le champ impacté est un select/radio
 *
 * Note: Les champs `id` et `path` sont initialisés vides et seront
 * enrichis plus tard par la fonction `enrichImpacts`
 *
 * @param rawImpact - Les données brutes de l'impact (format inconnu)
 * @returns Un impact normalisé prêt à être utilisé
 *
 * @example
 * // Impact simple qui cache un champ
 * buildImpact({
 *   name: "fieldToHide",
 *   hidden: true
 * })
 * // Retourne: {
 * //   id: "",
 * //   path: [],
 * //   name: "fieldToHide",
 * //   properties: { hidden: true }
 * // }
 *
 * @example
 * // Impact qui modifie les options d'un select
 * buildImpact({
 *   name: "countrySelect",
 *   options: [
 *     { id: "fr", text: "France" },
 *     { id: "uk", text: "United Kingdom" }
 *   ]
 * })
 * // Retourne: {
 * //   id: "",
 * //   path: [],
 * //   name: "countrySelect",
 * //   properties: {
 * //     options: [
 * //       { id: "fr", value: "France" },
 * //       { id: "uk", value: "United Kingdom" }
 * //     ]
 * //   }
 * // }
 */
export const buildImpact = (rawImpact: unknown): FieldImpact => {
  // Étape 1: Validation de l'entrée
  if (!isValidImpactInput(rawImpact)) {
    return DEFAULT_EMPTY_IMPACT;
  }

  // Typer l'impact brut pour un accès plus facile
  const rawImpactData = rawImpact as RawImpactData;

  // Étape 2: Extraire le nom du champ cible
  const targetFieldName = extractTargetFieldName(rawImpactData);

  // Étape 3: Extraire les propriétés à appliquer (sans le nom)
  const impactProperties = extractImpactProperties(rawImpactData);

  // Étape 4: Traiter les options si présentes
  const processedProperties = processOptionsIfPresent(impactProperties);

  // Étape 5: Construire l'impact final
  const fieldImpact = createFieldImpact(targetFieldName, processedProperties);

  return fieldImpact;
};

// ========================================
// VALIDATION
// ========================================

/**
 * Vérifie si l'entrée est valide pour créer un impact
 *
 * @param input - La valeur à vérifier
 * @returns true si l'entrée peut être utilisée pour créer un impact
 */
function isValidImpactInput(input: unknown): boolean {
  return isObject(input);
}

// ========================================
// EXTRACTION DES DONNÉES
// ========================================

/**
 * Extrait le nom du champ qui sera impacté
 *
 * @param rawImpactData - Les données brutes de l'impact
 * @returns Le nom du champ cible ou une chaîne vide si non trouvé
 */
function extractTargetFieldName(rawImpactData: RawImpactData): string {
  return getString(rawImpactData as Record<string, unknown>, "name");
}

/**
 * Extrait toutes les propriétés de l'impact sauf le nom
 *
 * Le nom est une métadonnée qui indique quel champ cibler,
 * les autres propriétés sont celles qui seront appliquées au champ
 *
 * @param rawImpactData - Les données brutes de l'impact
 * @returns Les propriétés à appliquer au champ cible
 */
function extractImpactProperties(
  rawImpactData: RawImpactData,
): Record<string, unknown> {
  // Déstructurer pour séparer le nom des autres propriétés
  const { name: _nameFieldIgnored, ...propertiesWithoutName } = rawImpactData;

  return propertiesWithoutName;
}

// ========================================
// TRAITEMENT DES OPTIONS
// ========================================

/**
 * Traite les options si elles sont présentes dans les propriétés
 *
 * Si le champ impacté est un select/radio, ses options peuvent être
 * modifiées dynamiquement. Cette fonction normalise ces options.
 *
 * @param properties - Les propriétés de l'impact
 * @returns Les propriétés avec les options normalisées si présentes
 */
function processOptionsIfPresent(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  // Vérifier si des options sont présentes
  if (!hasOptionsProperty(properties)) {
    return properties;
  }

  // Extraire et normaliser les options
  const normalizedOptions = normalizeOptions(properties.options);

  // Retourner les propriétés avec les options normalisées
  return {
    ...properties,
    options: normalizedOptions,
  };
}

/**
 * Vérifie si les propriétés contiennent des options
 *
 * @param properties - Les propriétés à vérifier
 * @returns true si des options sont présentes et sont un tableau
 */
function hasOptionsProperty(properties: Record<string, unknown>): boolean {
  return Array.isArray(properties.options);
}

/**
 * Normalise un tableau d'options brutes
 *
 * Filtre les éléments invalides et construit des options normalisées
 *
 * @param rawOptions - Les options brutes à normaliser
 * @returns Un tableau d'options normalisées
 */
function normalizeOptions(rawOptions: unknown): FormOption[] {
  if (!Array.isArray(rawOptions)) {
    return [];
  }

  // Filtrer les éléments non-objets et construire les options
  const validRawOptions = filterValidOptionObjects(rawOptions);
  const normalizedOptions = buildOptionsFromRawData(validRawOptions);

  return normalizedOptions;
}

/**
 * Filtre les éléments valides (objets) d'un tableau d'options
 *
 * @param rawOptions - Le tableau d'options brutes
 * @returns Uniquement les éléments qui sont des objets
 */
function filterValidOptionObjects(rawOptions: unknown[]): unknown[] {
  return rawOptions.filter((item) => isObject(item));
}

/**
 * Construit des options normalisées à partir de données brutes
 *
 * @param rawOptionsData - Les données brutes des options
 * @returns Les options normalisées
 */
function buildOptionsFromRawData(rawOptionsData: unknown[]): FormOption[] {
  return rawOptionsData.map((rawOption) => buildOption(rawOption));
}

// ========================================
// CRÉATION DE L'IMPACT
// ========================================

/**
 * Crée l'objet FieldImpact final
 *
 * Note: Les champs `id` et `path` sont initialisés vides car ils seront
 * remplis plus tard lors de l'enrichissement (voir enrichImpacts)
 *
 * @param targetFieldName - Le nom du champ cible
 * @param properties - Les propriétés à appliquer
 * @returns L'impact structuré
 */
function createFieldImpact(
  targetFieldName: string,
  properties: Record<string, unknown>,
): FieldImpact {
  return {
    // Ces valeurs seront enrichies plus tard
    id: "",
    path: [],

    // Le nom du champ à impacter
    name: targetFieldName,

    // Les propriétés à appliquer au champ
    properties: properties as Partial<RawField>,
  };
}

// ========================================
// FONCTIONS UTILITAIRES EXPORTÉES
// ========================================

/**
 * Construit plusieurs impacts à partir d'un tableau de données brutes
 *
 * @param rawImpacts - Tableau d'impacts bruts
 * @returns Tableau d'impacts normalisés
 *
 * @example
 * buildMultipleImpacts([
 *   { name: "field1", hidden: true },
 *   { name: "field2", required: true }
 * ])
 * // Retourne deux impacts normalisés
 */
export function buildMultipleImpacts(rawImpacts: unknown[]): FieldImpact[] {
  if (!Array.isArray(rawImpacts)) {
    return [];
  }

  return rawImpacts
    .filter(isValidImpactInput)
    .map((rawImpact) => buildImpact(rawImpact));
}

/**
 * Vérifie si un impact est valide et complet
 *
 * @param impact - L'impact à vérifier
 * @returns true si l'impact a un nom valide
 */
export function isValidImpact(impact: FieldImpact): boolean {
  return impact.name !== "" && impact.name !== undefined;
}
